import { taskPoller } from '../utils/taskPoller'
import { extractClassifyAndIngestCelery } from '../utils/utils'

/**
 * Source Document Status States
 */
export const SourceDocumentStatus = {
  WAITING: 'waiting',
  UPLOADING: 'uploading', 
  REVIEW: 'review'
}

/**
 * Global Source Document Status Manager
 * Persists source document statuses and manages polling independent of UI state
 */
class SourceDocumentStatusManager {
  constructor() {
    this.documentStatuses = new Map() // documentId -> status
    this.documentFilePaths = new Map() // documentId -> { filePath, connectorId, createdAt }
    this.activePolls = new Map() // documentId -> { taskId, orgAccessToken }
    this.listeners = new Set() // Event listeners
    this.storageKey = 'documentStatuses_v1'
    this.pollsStorageKey = 'activePolls_v1'
    this.filePathsStorageKey = 'documentFilePaths_v1'
    this.healthCheckInterval = null
    this.backgroundSyncInterval = null
    this.isTabVisible = true
    
    this.init()
  }

  /**
   * Initialize the manager
   */
  init() {
    this.loadFromStorage()
    this.loadFilePathsFromStorage()
    this.logReviewDocumentsFromStorage()
    this.restoreActivePolls()
    
    // Handle page visibility changes for optimized background polling
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.handleVisibilityChange()
      })
      
      // Set initial visibility state
      this.isTabVisible = document.visibilityState === 'visible'
    }

    // Handle page unload - save state
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.saveToStorage()
      })
    }

    // Start periodic health check for polling tasks
    this.startHealthCheck()
    
    // Start background sync for inactive tab handling
    this.startBackgroundSync()

    // Emit initial state loaded event
    setTimeout(() => {
      this.emit('initialized', {
        documentsLoaded: this.documentStatuses.size,
        activePolls: this.activePolls.size
      })
    }, 100) // Small delay to ensure everything is loaded
  }

  /**
   * Add event listener for status changes
   */
  addEventListener(callback) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  /**
   * Emit status change event
   */
  emit(event, data) {
    const eventData = {
      type: event,
      timestamp: new Date(),
      data
    }
    
    this.listeners.forEach(listener => {
      try {
        listener(eventData)
      } catch (error) {
        console.error('Document status listener error:', error)
      }
    })
  }

  /**
   * Get document status
   */
  getStatus(documentId) {
    return this.documentStatuses.get(documentId) || SourceDocumentStatus.WAITING
  }

  /**
   * Set document status
   */
  setStatus(documentId, status) {
    const oldStatus = this.documentStatuses.get(documentId)
    if (oldStatus !== status) {
      this.documentStatuses.set(documentId, status)
      this.saveToStorage()
      
      console.log(`Document ${documentId} status: ${oldStatus || 'undefined'} → ${status}`)
      
      // Log all objects when status changes to REVIEW
      if (status === SourceDocumentStatus.REVIEW) {
        const filePathRelationship = this.getDocumentFilePath(documentId)
        const pollInfo = this.getPollInfo(documentId)
        console.log(`📋 Document ${documentId} REVIEW Status - All Associated Objects:`, {
          documentId,
          oldStatus,
          newStatus: status,
          filePathRelationship,
          pollInfo,
          allStatuses: Object.fromEntries(this.documentStatuses),
          allFilePaths: Object.fromEntries(this.documentFilePaths),
          timestamp: new Date()
        })
      }
      
      this.emit('statusChanged', {
        documentId,
        oldStatus,
        newStatus: status
      })
    }
  }

  /**
   * Set document file path relationship
   */
  setDocumentFilePath(documentId, filePath, connectorId) {
    const relationship = {
      filePath,
      connectorId,
      createdAt: new Date()
    }
    
    this.documentFilePaths.set(documentId, relationship)
    this.saveFilePathsToStorage()
    
    console.log(`Created file path relationship: ${filePath} -> document ${documentId}`)
    
    this.emit('filePathSet', {
      documentId,
      filePath,
      connectorId
    })
  }

  /**
   * Get document file path
   */
  getDocumentFilePath(documentId) {
    return this.documentFilePaths.get(documentId) || null
  }

  /**
   * Remove document file path relationship
   */
  removeDocumentFilePath(documentId) {
    const relationship = this.documentFilePaths.get(documentId)
    if (relationship) {
      this.documentFilePaths.delete(documentId)
      this.saveFilePathsToStorage()
      
      console.log(`Removed file path relationship for document ${documentId}`)
      
      this.emit('filePathRemoved', {
        documentId,
        filePath: relationship.filePath
      })
      
      return relationship
    }
    return null
  }

  /**
   * Find document by file path
   */
  findDocumentByFilePath(filePath) {
    for (const [documentId, relationship] of this.documentFilePaths.entries()) {
      if (relationship.filePath === filePath) {
        return documentId
      }
    }
    return null
  }

  /**
   * Start document ingestion with persistent polling
   */
  async startIngestion(config) {
    const {
      documentId,
      filePaths,
      connectorId,
      orgAccessToken,
      onProgress,
      onSuccess,
      onError
    } = config

    try {
      console.log(`Starting ingestion for document ${documentId}`)
      
      // Set status to uploading
      this.setStatus(documentId, SourceDocumentStatus.UPLOADING)

      // Call the ingestion API
      const payload = {
        file_paths: filePaths,
        document_set_key: connectorId
      }

      console.log('payload', payload)

      const response = await extractClassifyAndIngestCelery(payload, orgAccessToken)
      console.log('response', response)
      console.log(`Ingestion API response for ${documentId}:`, response)

      if (response && response.task_id) {
        // Store active poll info
        this.activePolls.set(documentId, {
          taskId: response.task_id,
          orgAccessToken,
          startTime: new Date(),
          config: {
            onProgress,
            onSuccess,
            onError
          }
        })
        
        this.savePolls()

        // Start polling with persistent callback
        this.startPollingForDocument(documentId, response.task_id, orgAccessToken, {
          onProgress,
          onSuccess,
          onError
        })

        console.log(`Started persistent polling for document ${documentId}, task ${response.task_id}`)
        
        return response.task_id
      } else {
        throw new Error('No task_id returned from API')
      }
    } catch (error) {
      console.error(`Ingestion failed for document ${documentId}:`, error)
      this.setStatus(documentId, SourceDocumentStatus.WAITING)
      
      if (config.onError) {
        config.onError(error)
      }
      
      throw error
    }
  }

  /**
   * Start polling for a specific document
   */
  startPollingForDocument(documentId, taskId, orgAccessToken, callbacks = {}, intervalMs = null) {
    const handleTaskStatusChange = (polledTaskId, state) => {
      console.log(`Document ${documentId} task ${polledTaskId} state: ${state}`)
      
      // Call progress callback if provided
      if (callbacks.onProgress) {
        const progress = this.getProgressFromState(state)
        callbacks.onProgress(progress, state)
      }

      switch (state) {
        case 'PENDING':
        case 'STARTED':
        case 'RETRY':
          // Keep status as uploading during task processing
          // Status was already set to uploading when ingestion started
          break
          
        case 'SUCCESS':
          this.setStatus(documentId, SourceDocumentStatus.REVIEW)
          
          // Log all objects associated with this documentId when status reaches "review"
          const filePathRelationship = this.getDocumentFilePath(documentId)
          const pollInfo = this.getPollInfo(documentId)
          console.log(`📋 Document ${documentId} reached REVIEW status - Associated Objects:`, {
            documentId,
            status: SourceDocumentStatus.REVIEW,
            filePathRelationship,
            pollInfo,
            timestamp: new Date()
          })
          
          // File path relationship persists on successful ingestion
          this.completePolling(documentId, true, null, callbacks.onSuccess)
          break
          
        case 'ERROR':
        case 'FAILURE':
          this.setStatus(documentId, SourceDocumentStatus.WAITING)
          // Remove file path relationship on ingestion failure
          this.removeDocumentFilePath(documentId)
          const error = new Error(`Task failed with status: ${state}`)
          this.completePolling(documentId, false, error, callbacks.onError)
          break
          
        default:
          console.warn(`Unknown task state for document ${documentId}: ${state}`)
      }
    }

    // Start persistent polling with appropriate interval
    const pollingInterval = intervalMs || this.getOptimalPollingInterval()
    taskPoller.startPolling(taskId, orgAccessToken, handleTaskStatusChange, pollingInterval)
  }

  /**
   * Complete polling for a document
   */
  completePolling(documentId, success, error = null, callback = null) {
    console.log(`Completing polling for document ${documentId}, success: ${success}`)
    
    // Remove from active polls
    const pollInfo = this.activePolls.get(documentId)
    if (pollInfo) {
      taskPoller.stopPolling(pollInfo.taskId)
      this.activePolls.delete(documentId)
      this.savePolls()
    }

    // Call callback if provided
    if (callback) {
      if (success) {
        callback({ documentId, status: this.getStatus(documentId) })
      } else {
        callback(error)
      }
    }

    this.emit('pollingCompleted', {
      documentId,
      success,
      error,
      finalStatus: this.getStatus(documentId)
    })
  }

  /**
   * Get progress percentage from Celery task state
   */
  getProgressFromState(state) {
    switch (state) {
      case 'PENDING': return 10
      case 'STARTED': return 30
      case 'RETRY': return 15
      case 'SUCCESS': return 100
      case 'ERROR':
      case 'FAILURE': return 0
      default: return 25
    }
  }

  /**
   * Cancel polling for a document
   */
  cancelPolling(documentId) {
    console.log(`Cancelling polling for document ${documentId}`)
    
    const pollInfo = this.activePolls.get(documentId)
    if (pollInfo) {
      const stopped = taskPoller.stopPolling(pollInfo.taskId)
      if (!stopped) {
        // If normal stop failed, try force stop
        console.warn(`Normal stop failed for task ${pollInfo.taskId}, trying force stop`)
        taskPoller.forceStopPolling(pollInfo.taskId)
      }
      
      this.activePolls.delete(documentId)
      this.savePolls()
      
      // Reset status to waiting
      this.setStatus(documentId, SourceDocumentStatus.WAITING)
      
      // Remove file path relationship on polling cancellation
      this.removeDocumentFilePath(documentId)
      
      this.emit('pollingCancelled', { documentId })
    } else {
      console.warn(`No polling info found for document ${documentId}`)
    }
  }

  /**
   * Cancel all active polling and reset all document statuses to waiting
   */
  cancelAllPolling() {
    console.log('Cancelling all active polling')
    
    const activeDocuments = Array.from(this.activePolls.keys())
    const stoppedTasks = []
    const failedStops = []
    
    // Stop all active polling
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      const stopped = taskPoller.stopPolling(pollInfo.taskId)
      if (stopped) {
        stoppedTasks.push(pollInfo.taskId)
      } else {
        // Try force stop
        console.warn(`Normal stop failed for task ${pollInfo.taskId}, trying force stop`)
        const forceStop = taskPoller.forceStopPolling(pollInfo.taskId)
        if (forceStop) {
          stoppedTasks.push(pollInfo.taskId)
        } else {
          failedStops.push(pollInfo.taskId)
        }
      }
      
      // Reset status to waiting regardless of stop success
      this.setStatus(documentId, SourceDocumentStatus.WAITING)
      
      // Remove file path relationship when cancelling polling
      this.removeDocumentFilePath(documentId)
    }
    
    // Force stop any remaining polling tasks that might be orphaned
    const taskPollerResult = taskPoller.forceStopAllPolling()
    
    // Clear all active polls
    this.activePolls.clear()
    this.savePolls()
    
    const result = {
      cancelledDocuments: activeDocuments,
      count: activeDocuments.length,
      stoppedTasks: stoppedTasks.length,
      failedStops: failedStops.length,
      taskPollerResult
    }
    
    this.emit('allPollingCancelled', result)
    
    console.log(`Cancelled polling for ${activeDocuments.length} documents`, result)
    
    return activeDocuments.length
  }

  /**
   * Force stop polling for a specific task ID (direct taskPoller access)
   * @param {string} taskId - The Celery task ID to force stop
   */
  forceStopPollingByTaskId(taskId) {
    console.log(`Force stopping polling for task ID: ${taskId}`)
    
    // First try to find the document that owns this task
    let ownerDocumentId = null
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      if (pollInfo.taskId === taskId) {
        ownerDocumentId = documentId
        break
      }
    }
    
    // Force stop the polling task
    const stopped = taskPoller.forceStopPolling(taskId)
    
    // Clean up the document status manager state
    if (ownerDocumentId) {
      this.activePolls.delete(ownerDocumentId)
      this.setStatus(ownerDocumentId, SourceDocumentStatus.WAITING)
      this.savePolls()
      
      this.emit('pollingForceStopped', { documentId: ownerDocumentId, taskId })
      console.log(`Cleaned up document ${ownerDocumentId} after force stopping task ${taskId}`)
    } else {
      console.warn(`No document found owning task ${taskId}, but stopped polling anyway`)
    }
    
    return {
      stopped,
      ownerDocumentId,
      taskId
    }
  }

  /**
   * Get all active polling documents
   */
  getActivePollingDocuments() {
    return Array.from(this.activePolls.keys())
  }

  /**
   * Check if document is actively being polled
   */
  isPolling(documentId) {
    return this.activePolls.has(documentId)
  }

  /**
   * Get poll info for a document
   */
  getPollInfo(documentId) {
    return this.activePolls.get(documentId) || null
  }

  /**
   * Handle visibility state changes for optimized background polling
   */
  handleVisibilityChange() {
    const wasVisible = this.isTabVisible
    this.isTabVisible = document.visibilityState === 'visible'
    
    console.log(`Tab visibility changed: ${wasVisible ? 'visible' : 'hidden'} → ${this.isTabVisible ? 'visible' : 'hidden'}`)
    
    if (this.isTabVisible && !wasVisible) {
      // Tab became visible - sync status and resume optimized polling
      this.syncStatusFromBackground()
      this.optimizePollingForVisibility()
    } else if (!this.isTabVisible && wasVisible) {
      // Tab became hidden - switch to background polling mode
      this.optimizePollingForVisibility()
    }
  }

  /**
   * Optimize polling intervals based on tab visibility
   */
  optimizePollingForVisibility() {
    console.log(`Optimizing polling for ${this.isTabVisible ? 'visible' : 'hidden'} tab`)
    
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      // Restart polling with visibility-optimized intervals
      if (taskPoller.isPolling(pollInfo.taskId)) {
        taskPoller.stopPolling(pollInfo.taskId)
      }
      
      const interval = this.getOptimalPollingInterval()
      this.startPollingForDocument(
        documentId,
        pollInfo.taskId,
        pollInfo.orgAccessToken,
        pollInfo.config || {},
        interval
      )
    }
  }

  /**
   * Get optimal polling interval based on tab visibility
   */
  getOptimalPollingInterval() {
    // Visible tab: poll more frequently for responsive UI
    // Hidden tab: poll less frequently to save resources but still catch completions
    return this.isTabVisible ? 2000 : 10000 // 2s when visible, 10s when hidden
  }

  /**
   * Start background synchronization for inactive tab status updates
   */
  startBackgroundSync() {
    // Sync every 30 seconds to catch any missed updates
    this.backgroundSyncInterval = setInterval(() => {
      if (!this.isTabVisible && this.activePolls.size > 0) {
        this.performBackgroundSync()
      }
    }, 30000)
  }

  /**
   * Perform background synchronization
   */
  async performBackgroundSync() {
    console.log('Performing background sync for inactive tab')
    
    const syncPromises = []
    
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      // Check task status directly via API
      syncPromises.push(this.checkTaskStatusDirect(documentId, pollInfo))
    }
    
    try {
      const results = await Promise.allSettled(syncPromises)
      const successful = results.filter(r => r.status === 'fulfilled').length
      console.log(`Background sync completed: ${successful}/${results.length} successful`)
      
      // Save any status changes to localStorage
      this.saveToStorage()
    } catch (error) {
      console.error('Background sync error:', error)
    }
  }

  /**
   * Check task status directly via API (for background sync)
   */
  async checkTaskStatusDirect(documentId, pollInfo) {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL_BASE
      const response = await fetch(`${apiBaseUrl}/tasks/${pollInfo.taskId}/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pollInfo.orgAccessToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const state = data.state
        
        console.log(`Background sync - Document ${documentId} task state: ${state}`)
        
        // Update status based on task state
        const statusChanged = this.updateStatusFromTaskState(documentId, state)
        
        // If task completed, clean up polling
        if (['SUCCESS', 'ERROR', 'FAILURE'].includes(state)) {
          this.completePolling(documentId, state === 'SUCCESS', 
            state !== 'SUCCESS' ? new Error(`Task failed with status: ${state}`) : null)
          return { documentId, completed: true, status: state }
        }
        
        return { documentId, status: state, changed: statusChanged }
      } else {
        console.error(`Background sync failed for document ${documentId}: ${response.statusText}`)
        return { documentId, error: response.statusText }
      }
    } catch (error) {
      console.error(`Background sync error for document ${documentId}:`, error)
      return { documentId, error: error.message }
    }
  }

  /**
   * Update document status based on task state
   */
  updateStatusFromTaskState(documentId, taskState) {
    let newStatus = null
    
    switch (taskState) {
      case 'PENDING':
      case 'STARTED':
      case 'RETRY':
        newStatus = SourceDocumentStatus.UPLOADING
        break
      case 'SUCCESS':
        newStatus = SourceDocumentStatus.REVIEW
        break
      case 'ERROR':
      case 'FAILURE':
        newStatus = SourceDocumentStatus.WAITING
        break
      default:
        newStatus = SourceDocumentStatus.UPLOADING
        break
    }
    
    if (newStatus && this.getStatus(documentId) !== newStatus) {
      this.setStatus(documentId, newStatus)
      return true
    }
    return false
  }

  /**
   * Sync status when tab becomes visible (catch any missed updates)
   */
  async syncStatusFromBackground() {
    console.log('Syncing status from background on tab visibility change')
    
    if (this.activePolls.size > 0) {
      // Perform immediate sync check
      await this.performBackgroundSync()
      
      this.emit('backgroundSync', {
        activePolls: this.activePolls.size,
        timestamp: new Date()
      })
    }
  }

  /**
   * Stop background sync
   */
  stopBackgroundSync() {
    if (this.backgroundSyncInterval) {
      clearInterval(this.backgroundSyncInterval)
      this.backgroundSyncInterval = null
    }
  }

  /**
   * Resume all active polling (legacy method - now uses optimized approach)
   */
  resumeAllPolling() {
    console.log('Resuming all active polling (legacy call - using optimized approach)')
    this.optimizePollingForVisibility()
  }

  /**
   * Reset document status to waiting
   */
  resetStatus(documentId) {
    console.log(`Resetting status for document ${documentId}`)
    
    // Cancel any active polling
    this.cancelPolling(documentId)
    
    // Set status to waiting
    this.setStatus(documentId, SourceDocumentStatus.WAITING)
  }

  /**
   * Get all document statuses
   */
  getAllStatuses() {
    return Object.fromEntries(this.documentStatuses)
  }

  /**
   * Get documents by status
   */
  getSourceDocumentsByStatus(status) {
    const result = []
    for (const [documentId, docStatus] of this.documentStatuses.entries()) {
      if (docStatus === status) {
        result.push(documentId)
      }
    }
    return result
  }

  /**
   * Clear all statuses and polling
   */
  clearAll() {
    console.log('Clearing all document statuses and polling')
    
    // Stop all active polling
    for (const [documentId] of this.activePolls.entries()) {
      this.cancelPolling(documentId)
    }
    
    // Clear all statuses and file paths
    this.documentStatuses.clear()
    this.documentFilePaths.clear()
    this.activePolls.clear()
    
    // Clear storage
    this.saveToStorage()
    this.saveFilePathsToStorage()
    this.savePolls()
    
    this.emit('cleared', {})
  }

  /**
   * Save document statuses to localStorage
   */
  saveToStorage() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const data = {
        statuses: Object.fromEntries(this.documentStatuses),
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save document statuses to storage:', error)
    }
  }

  /**
   * Load document statuses from localStorage
   */
  loadFromStorage() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const data = JSON.parse(localStorage.getItem(this.storageKey) || '{}')
      
      if (data.statuses) {
        this.documentStatuses = new Map(Object.entries(data.statuses))
        console.log(`Loaded ${this.documentStatuses.size} document statuses from storage`)
      }
    } catch (error) {
      console.error('Failed to load document statuses from storage:', error)
    }
  }

  /**
   * Save document file paths to localStorage
   */
  saveFilePathsToStorage() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const data = {
        filePaths: Object.fromEntries(this.documentFilePaths),
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem(this.filePathsStorageKey, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save document file paths to storage:', error)
    }
  }

  /**
   * Load document file paths from localStorage
   */
  loadFilePathsFromStorage() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const data = JSON.parse(localStorage.getItem(this.filePathsStorageKey) || '{}')
      
      if (data.filePaths) {
        // Convert date strings back to Date objects
        const filePathsWithDates = Object.entries(data.filePaths).map(([documentId, relationship]) => [
          documentId,
          {
            ...relationship,
            createdAt: new Date(relationship.createdAt)
          }
        ])
        this.documentFilePaths = new Map(filePathsWithDates)
        console.log(`Loaded ${this.documentFilePaths.size} document file paths from storage`)
      }
    } catch (error) {
      console.error('Failed to load document file paths from storage:', error)
    }
  }

  /**
   * Log all documents with REVIEW status after loading from storage
   * Called after both statuses and file paths are loaded
   */
  logReviewDocumentsFromStorage() {
    console.log(`📂 Checking loaded documents for REVIEW status...`)
    
    for (const [documentId, status] of this.documentStatuses.entries()) {
      if (status === SourceDocumentStatus.REVIEW) {
        const filePathRelationship = this.getDocumentFilePath(documentId)
        
        console.log(`📂 Document ${documentId} LOADED from localStorage with REVIEW status - All Associated Objects:`, {
          documentId,
          status: SourceDocumentStatus.REVIEW,
          loadedFromStorage: true,
          filePathRelationship,
          hasFilePathRelationship: !!filePathRelationship,
          allStatuses: Object.fromEntries(this.documentStatuses),
          allFilePaths: Object.fromEntries(this.documentFilePaths),
          loadTimestamp: new Date()
        })
      }
    }
  }

  /**
   * Save active polls to localStorage
   */
  savePolls() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const pollsData = {}
      for (const [documentId, pollInfo] of this.activePolls.entries()) {
        pollsData[documentId] = {
          taskId: pollInfo.taskId,
          orgAccessToken: pollInfo.orgAccessToken,
          startTime: pollInfo.startTime.toISOString()
          // Note: callbacks are not serializable, they'll need to be re-attached
        }
      }
      
      const data = {
        polls: pollsData,
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem(this.pollsStorageKey, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save active polls to storage:', error)
    }
  }

  /**
   * Restore active polls from localStorage (for page refresh recovery)
   */
  restoreActivePolls() {
    if (typeof localStorage === 'undefined') return
    
    try {
      const data = JSON.parse(localStorage.getItem(this.pollsStorageKey) || '{}')
      
      if (data.polls) {
        for (const [documentId, pollData] of Object.entries(data.polls)) {
          const pollInfo = {
            taskId: pollData.taskId,
            orgAccessToken: pollData.orgAccessToken,
            startTime: new Date(pollData.startTime),
            config: {} // Callbacks will be re-attached when components mount
          }
          
          this.activePolls.set(documentId, pollInfo)
          
          // Check if this poll should still be active (not too old)
          const elapsed = new Date() - pollInfo.startTime
          const maxAge = 30 * 60 * 1000 // 30 minutes
          
          if (elapsed < maxAge) {
            console.log(`Restoring polling for document ${documentId}, task ${pollInfo.taskId}`)
            
            // Start polling without callbacks (they'll be added when components mount)
            this.startPollingForDocument(documentId, pollInfo.taskId, pollInfo.orgAccessToken)
          } else {
            console.log(`Polling for document ${documentId} expired (${Math.round(elapsed / 60000)} minutes old), removing`)
            this.activePolls.delete(documentId)
          }
        }
        
        // Save updated polls (removes expired ones)
        this.savePolls()
        
        console.log(`Restored ${this.activePolls.size} active polls from storage`)
      }
    } catch (error) {
      console.error('Failed to restore active polls from storage:', error)
    }
  }

  /**
   * Get debugging information about polling state
   */
  getPollingDebugInfo() {
    const documentManagerPolls = {}
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      documentManagerPolls[documentId] = {
        taskId: pollInfo.taskId,
        startTime: pollInfo.startTime,
        hasOrgToken: !!pollInfo.orgAccessToken
      }
    }
    
    const taskPollerDetails = taskPoller.getPollingDetails()
    
    return {
      documentManager: {
        activePolls: this.activePolls.size,
        polls: documentManagerPolls
      },
      taskPoller: taskPollerDetails,
      mismatch: {
        // Find tasks in taskPoller but not in documentManager
        orphanedTasks: taskPollerDetails.details ? 
          Object.keys(taskPollerDetails.details).filter(taskId => 
            !Object.values(documentManagerPolls).some(poll => poll.taskId === taskId)
          ) : [],
        // Find documents in documentManager but not in taskPoller  
        unmappedDocuments: Object.entries(documentManagerPolls)
          .filter(([docId, poll]) => !taskPoller.isPolling(poll.taskId))
          .map(([docId]) => docId)
      }
    }
  }

  /**
   * Get statistics
   */
  getStats() {
    const statuses = Array.from(this.documentStatuses.values())
    
    return {
      totalDocuments: this.documentStatuses.size,
      waiting: statuses.filter(s => s === SourceDocumentStatus.WAITING).length,
      uploading: statuses.filter(s => s === SourceDocumentStatus.UPLOADING).length,
      review: statuses.filter(s => s === SourceDocumentStatus.REVIEW).length,
      activePolls: this.activePolls.size
    }
  }

  /**
   * Start periodic health check for polling tasks
   */
  startHealthCheck() {
    // Run health check every 5 minutes
    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck()
    }, 5 * 60 * 1000)
  }

  /**
   * Perform health check on active polls
   */
  performHealthCheck() {
    console.log('Performing health check on active polls')
    const now = new Date()
    const maxAge = 30 * 60 * 1000 // 30 minutes
    const stalePolls = []
    
    for (const [documentId, pollInfo] of this.activePolls.entries()) {
      const elapsed = now - pollInfo.startTime
      
      // Check if poll is too old
      if (elapsed > maxAge) {
        console.log(`Poll for document ${documentId} is stale (${Math.round(elapsed / 60000)} minutes old)`)
        stalePolls.push(documentId)
        continue
      }
      
      // Check if taskPoller is still tracking this task
      if (!taskPoller.isPolling(pollInfo.taskId)) {
        console.log(`TaskPoller is not tracking task ${pollInfo.taskId} for document ${documentId}`)
        stalePolls.push(documentId)
      }
    }
    
    // Clean up stale polls
    for (const documentId of stalePolls) {
      console.log(`Cleaning up stale poll for document ${documentId}`)
      this.cancelPolling(documentId)
    }
    
    if (stalePolls.length > 0) {
      this.emit('healthCheck', {
        stalePollsCleaned: stalePolls.length,
        stalePolls
      })
    }
  }

  /**
   * Stop health check
   */
  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
  }


  /**
   * Destroy the manager
   */
  destroy() {
    console.log('Destroying document status manager')
    
    // Stop health check
    this.stopHealthCheck()
    
    // Stop background sync
    this.stopBackgroundSync()
    
    // Cancel all polling
    for (const pollInfo of this.activePolls.values()) {
      taskPoller.stopPolling(pollInfo.taskId)
    }
    
    // Clear listeners
    this.listeners.clear()
    
    // Save final state
    this.saveToStorage()
    this.savePolls()
  }
}

// Export singleton instance
export const sourceDocumentStatusManager = new SourceDocumentStatusManager()